<!--
  ApprovalThread.vue (Phase 2.1 -- card https://trello.com/c/hYlwyKYT).

  Role-attributed, append-only message thread on an ApprovalRecord.
  Reads ``thread_messages`` straight off the detail payload returned by
  GET /api/approval_records/<id> (F1.e). Each message renders with:

    - role badge (Builder / Digitizer / Store owner / System)
    - author name (when known)
    - body text
    - timestamp (ISO -> short relative; falls back to raw ISO)

  System messages get a distinct visual treatment (italic, no role
  badge) per ADR 0040's "shared, role-scoped" guidance.

  Below the list: a textarea + Send button. The Send button posts to
  POST /api/approval_records/<id>/messages. The textarea clears on
  successful post.

  Props:
    - messages: array
    - canPost: boolean -- gates the input (capability + status checks
      live in the parent so the thread component stays presentation-only).
    - pending: boolean

  Emits:
    - post(body)

  Author resolution: the F1.e thread payload carries ``author_user_id``
  (nullable for system messages). Names are resolved client-side from
  whatever role/context the caller passes via ``authorNames``; when
  unknown the role label is shown alone.
-->
<template>
    <div class="aw-thread" data-testid="approval-thread">
        <div class="aw-thread__header">
            <span class="aw-thread__title">Approval thread</span>
            <span class="aw-thread__count"
                >{{ messages.length }}
                message{{ messages.length === 1 ? '' : 's' }}</span
            >
        </div>
        <div class="aw-thread__body">
            <div
                v-if="!messages.length"
                class="aw-thread__empty"
                data-testid="approval-thread-empty"
            >
                No messages yet.
            </div>
            <div
                v-for="msg in messages"
                :key="msg.id"
                class="aw-thread__msg"
                :class="`aw-thread__msg--${msg.author_role || 'system'}`"
                :data-testid="`approval-thread-msg-${msg.id}`"
            >
                <div class="aw-thread__msg-head">
                    <span
                        v-if="msg.author_role !== 'system'"
                        class="aw-thread__role"
                        >{{ roleLabel(msg.author_role) }}</span
                    >
                    <span
                        v-if="msg.author_role === 'system'"
                        class="aw-thread__role aw-thread__role--system"
                        >System</span
                    >
                    <span class="aw-thread__when">{{
                        formatWhen(msg.created_at)
                    }}</span>
                </div>
                <div class="aw-thread__msg-body">{{ msg.body }}</div>
            </div>
        </div>
        <div v-if="canPost" class="aw-thread__compose">
            <textarea
                v-model="draft"
                rows="2"
                class="aw-thread__textarea"
                placeholder="Write a message to the digitizer or attach to the audit trail"
                data-testid="approval-thread-textarea"
                :disabled="pending"
            ></textarea>
            <button
                type="button"
                class="aw-thread__send"
                :disabled="pending || !draft.trim()"
                data-testid="approval-thread-send"
                @click="onSend"
            >
                Send
            </button>
        </div>
    </div>
</template>

<script>
const ROLE_LABELS = {
    builder: 'Builder',
    digitizer: 'Digitizer',
    owner: 'Store owner',
    system: 'System',
}

export default {
    name: 'ApprovalThread',
    props: {
        messages: {
            type: Array,
            default: () => [],
        },
        canPost: {
            type: Boolean,
            default: false,
        },
        pending: {
            type: Boolean,
            default: false,
        },
    },
    emits: ['post'],
    data() {
        return {
            draft: '',
        }
    },
    methods: {
        roleLabel(role) {
            return ROLE_LABELS[role] || role || ''
        },
        formatWhen(iso) {
            if (!iso) return ''
            const d = new Date(iso)
            if (Number.isNaN(d.getTime())) return iso
            return d.toLocaleString(undefined, {
                month: 'short',
                day: 'numeric',
                hour: 'numeric',
                minute: '2-digit',
            })
        },
        async onSend() {
            const body = this.draft.trim()
            if (!body) return
            this.$emit('post', body)
            this.draft = ''
        },
    },
}
</script>

<style scoped>
.aw-thread {
    border: 1px solid var(--tf-border, #e2e8f0);
    border-radius: var(--tf-radius-sm, 6px);
    background: var(--tf-bg, #ffffff);
    overflow: hidden;
    margin-bottom: 16px;
}
.aw-thread__header {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 10px 14px;
    background: var(--tf-surface-alt, #f7fafc);
    border-bottom: 1px solid var(--tf-border, #e2e8f0);
    font-size: 12px;
}
.aw-thread__title {
    font-weight: 600;
    color: var(--tf-fg-1, #1a202c);
}
.aw-thread__count {
    color: var(--tf-fg-4, #5a6a82);
}
.aw-thread__body {
    padding: 8px 14px;
    max-height: 320px;
    overflow-y: auto;
}
.aw-thread__empty {
    padding: 24px 0;
    text-align: center;
    color: var(--tf-fg-4, #5a6a82);
    font-size: 13px;
}
.aw-thread__msg {
    padding: 8px 0;
    border-bottom: 1px solid var(--tf-border, #f1f5f9);
}
.aw-thread__msg:last-child {
    border-bottom: none;
}
.aw-thread__msg-head {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 4px;
    font-size: 11px;
}
.aw-thread__role {
    display: inline-flex;
    padding: 1px 8px;
    border-radius: 10px;
    font-weight: 500;
    background: var(--tf-surface-alt, #edf2f7);
    color: var(--tf-fg-3, #4a5568);
    text-transform: uppercase;
    letter-spacing: 0.04em;
    font-size: 10px;
}
.aw-thread__msg--builder .aw-thread__role {
    background: rgba(25, 118, 210, 0.14);
    color: #0d47a1;
}
.aw-thread__msg--digitizer .aw-thread__role {
    background: rgba(94, 53, 177, 0.14);
    color: #4a148c;
}
.aw-thread__msg--owner .aw-thread__role {
    background: rgba(56, 142, 60, 0.16);
    color: #2e7d32;
}
.aw-thread__role--system {
    background: transparent;
    color: var(--tf-fg-4, #5a6a82);
    font-style: italic;
}
.aw-thread__when {
    color: var(--tf-fg-4, #5a6a82);
}
.aw-thread__msg-body {
    font-size: 13px;
    color: var(--tf-fg-1, #1a202c);
    white-space: pre-wrap;
}
.aw-thread__msg--system .aw-thread__msg-body {
    color: var(--tf-fg-4, #5a6a82);
    font-style: italic;
}
.aw-thread__compose {
    display: flex;
    align-items: flex-end;
    gap: 8px;
    padding: 10px 14px;
    border-top: 1px solid var(--tf-border, #e2e8f0);
    background: var(--tf-surface-alt, #f7fafc);
}
.aw-thread__textarea {
    flex: 1;
    padding: 8px 10px;
    border: 1px solid var(--tf-border-strong, #cbd5e0);
    border-radius: 4px;
    font-family: inherit;
    font-size: 13px;
    resize: vertical;
    min-height: 56px;
}
.aw-thread__send {
    padding: 8px 16px;
    border: 1px solid var(--tf-primary, #1976d2);
    background: var(--tf-primary, #1976d2);
    color: #ffffff;
    border-radius: 4px;
    cursor: pointer;
}
.aw-thread__send:disabled {
    opacity: 0.55;
    cursor: not-allowed;
}
</style>
